import { registerAndroidDevice } from '@/common/apis/services/notifications/inbox';
import classNames from '@/common/utils/classNames';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { DsCard, ds } from '../designSystem';
import { sendDoctorHomeEvent } from '../utils/analytics';
import {
  isNativePushCapable,
  isNativePushEnabled,
  markNativePushEnabled,
  nativePushEnableUrl,
  rememberNativePushCapability,
  takeNativePushResult,
} from '../utils/nativePush';
import { BellIcon } from './icons';

export const NATIVE_PUSH_FEATURE = 'doctor-home-native-push';

/**
 * Doctor home card: turn on the Android app's native notifications. Shown only inside an app
 * build that supports it, behind a GrowthBook flag, until this phone is linked to the user.
 */
export const EnableNotificationsCard = ({ className }: { className?: string }) => {
  const isOn = useFeatureIsOn(NATIVE_PUSH_FEATURE);
  const userId = useUserInfoStore(state => state.info?.id);
  const [visible, setVisible] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    rememberNativePushCapability();
    const result = takeNativePushResult();
    if (result) {
      sendDoctorHomeEvent(userId, 'push_enable_result', { granted: result.granted, token: !!result.token });
      if (result.token) {
        // Link the phone even if the permission was refused: it can be turned on later in settings.
        registerAndroidDevice(result.token)
          .then(() => {
            if (!result.granted) return;
            markNativePushEnabled();
            setVisible(false);
            toast.success('اعلان‌ها فعال شد');
          })
          .catch(() => toast.error('فعال‌سازی اعلان‌ها انجام نشد؛ دوباره تلاش کنید'));
      }
      if (!result.granted) setDenied(true);
    }
    setVisible(isNativePushCapable() && !isNativePushEnabled());
    // userId only labels the analytics event; the URL answer must be consumed once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isOn || !visible) return null;

  const enable = () => {
    sendDoctorHomeEvent(userId, 'push_enable_click');
    window.location.href = nativePushEnableUrl(window.location.pathname + window.location.search);
  };

  return (
    <DsCard padding="sm" onClick={enable} ariaLabel="فعال‌سازی اعلان‌ها" className={className}>
      <div className={classNames('flex items-center', ds.layout.listRowGap)}>
        <span className={classNames(ds.icon.containerCircle, 'h-10 w-10')}>
          <BellIcon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className={ds.type.cardTitle}>فعال‌سازی اعلان‌ها</p>
          <p className={classNames(ds.type.caption, 'mt-0.5')}>
            {denied
              ? 'اجازهٔ اعلان داده نشد؛ از تنظیمات گوشی برای پذیرش۲۴ روشن کنید.'
              : 'نوبت‌ها و پیام‌های تازه را همان لحظه روی گوشی ببینید.'}
          </p>
        </div>
        <span className={ds.type.pillButton}>فعال‌سازی</span>
      </div>
    </DsCard>
  );
};
